/**
 * Auth Context
 *
 * Provides authentication state and methods for Auth0 integration
 * Wraps the native Auth0 context with application-specific logic
 */

import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenGetter } from '@/lib/auth/tokenManager';
import { getCurrentUserDonor, type Donor } from '@/services/api/donorService';

export interface AuthUser {
  sub: string; // Auth0 user ID
  email?: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
  [key: string]: unknown;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True once the token getter has been registered with the API client.
   * With cacheLocation:'localstorage', Auth0 may resolve isLoading:false on the
   * very first render. React fires child effects before parent effects, so hooks
   * in child components could fire API calls before setTokenGetter runs.
   * Gate auth-requiring hooks on this flag to avoid that race.
   */
  isAuthReady: boolean;
  user: AuthUser | undefined;
  donor: Donor | null;
  isDonorLoading: boolean;
  loginWithRedirect: (options?: any) => Promise<void>;
  logout: (options?: any) => void;
  getAccessToken: () => Promise<string>;
  getIdToken: () => Promise<string>;
  refetchDonor: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 * Wraps Auth0Provider and provides app-specific auth context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  const [donor, setDonor] = useState<Donor | null>(null);
  const [isDonorLoading, setIsDonorLoading] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Debug: Log user data when it changes
  useEffect(() => {
    if (user) {
      console.log('Auth0 User Data:', user);
      console.log('User Picture:', user.picture);
    }
  }, [user]);

  const getAccessToken = async (): Promise<string> => {
    // Only attempt to get token if user is authenticated
    if (!isAuthenticated) {
      return '';
    }

    try {
      const token = await getAccessTokenSilently({
        detailedResponse: false,
      });
      return token as string;
    } catch (error) {
      console.error('Error getting access token:', error);
      return '';
    }
  };

  const getIdToken = async (): Promise<string> => {
    // Only attempt to get token if user is authenticated
    if (!isAuthenticated) {
      return '';
    }

    try {
      const claims = await getIdTokenClaims();
      return claims?.__raw || '';
    } catch (error) {
      console.error('Error getting ID token:', error);
      return '';
    }
  };

  // Initialize auth for the API client.
  //
  // 1.  Wait until Auth0 finishes its internal checkSession / token-refresh
  //     (isLoading → false).  Until then, getAccessTokenSilently will reject
  //     because the Auth0Client hasn't initialised yet.
  //
  // 2.  Register the token getter so the Axios interceptor can attach Bearer tokens.
  //
  // 3.  For authenticated sessions, **verify** the token is actually obtainable.
  //     With cacheLocation:'localstorage', Auth0 may report isAuthenticated:true
  //     from cached user-info even when the refresh token has expired.  If the
  //     verification call fails the session is stale → redirect to login so the
  //     user gets fresh tokens (if the Auth0 server session is still alive this
  //     is seamless; otherwise the user sees the login page).
  //
  // isAuthReady stays false until all of the above succeeds, which means child
  // component hooks gated on isAuthReady will never fire API calls before the
  // token getter is both registered AND proven to work.
  useEffect(() => {
    // Don't do anything while Auth0 is still initialising
    if (isLoading) return;

    const initAuth = async () => {
      // Always register the getter first
      setTokenGetter(async () => {
        try {
          const token = await getAccessTokenSilently({ detailedResponse: false });
          return token as string;
        } catch {
          return '';
        }
      });

      if (isAuthenticated) {
        // Verify the token is actually obtainable
        try {
          const token = await getAccessTokenSilently({ detailedResponse: false });
          if (token) {
            console.log('[Auth] Token verified – session is valid');
            setIsAuthReady(true);
            return;
          }
        } catch (err) {
          console.warn('[Auth] getAccessTokenSilently failed for authenticated user:', err);
        }

        // Token not available despite isAuthenticated → stale session.
        // Redirect to Auth0 to re-authenticate. If the Auth0 server session is
        // still active the redirect will be seamless; otherwise the user sees
        // the login page.  A sessionStorage flag prevents infinite loops.
        const REDIRECT_KEY = '__auth_relogin_attempted__';
        if (!sessionStorage.getItem(REDIRECT_KEY)) {
          sessionStorage.setItem(REDIRECT_KEY, '1');
          console.warn('[Auth] Stale session detected – redirecting to login');
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search },
          });
          return; // page will navigate away
        }

        // Already attempted → avoid redirect loop.  Clear stale state.
        sessionStorage.removeItem(REDIRECT_KEY);
        console.warn('[Auth] Re-login attempt failed – logging out to clear stale state');
        logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }

      // Not authenticated – mark ready so public pages work
      setIsAuthReady(true);
    };

    initAuth();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout]);

  // Clear the redirect-loop guard after a successful authenticated + ready state
  useEffect(() => {
    if (isAuthReady && isAuthenticated) {
      sessionStorage.removeItem('__auth_relogin_attempted__');
    }
  }, [isAuthReady, isAuthenticated]);

  // Fetch donor information when user logs in
  const fetchDonor = async () => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    setIsDonorLoading(true);
    try {
      const response = await getCurrentUserDonor();
      // Response contains item (Donor | null), status, and optional message
      setDonor(response.item);
      if (response.status === 'pending') {
        console.log('Donor profile pending approval:', response.message);
      } else {
        console.log('Donor profile loaded:', response.item);
      }
    } catch (error) {
      console.error('Error fetching donor profile:', error);
      // Don't throw - donor profile is optional
      setDonor(null);
    } finally {
      setIsDonorLoading(false);
    }
  };

  // Fetch donor when user authenticates
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchDonor();
    } else {
      setDonor(null);
    }
  }, [isAuthenticated, isLoading]);

  const refetchDonor = async () => {
    await fetchDonor();
  };

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    isAuthReady,
    user: user as AuthUser | undefined,
    donor,
    isDonorLoading,
    loginWithRedirect,
    logout,
    getAccessToken,
    getIdToken,
    refetchDonor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 * Must be used within AuthProvider
 *
 * @returns Auth context with user state and methods
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
